import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { api } from "../api/client";
import { qk, useUnreadCount } from "../api/queries";
import type { Notification } from "../api/types";
import { cx } from "../lib/format";
import { Icon } from "./Icon";
import { RelativeTime } from "./RelativeTime";
import { Spinner } from "./Spinner";

const KIND_LABEL: Record<string, string> = {
  human_review: "Review requested",
  agent_question: "Agent question",
  needs_attention: "Needs attention",
  attention: "Needs attention",
  post_merge_failure: "Post-merge check failed",
  post_merge_check: "Post-merge check",
  proposal: "Task proposal",
  proposal_created: "Task proposal",
};

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { data: unread } = useUnreadCount();
  const count = unread?.count ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="bell" ref={ref}>
      <button
        className={cx("sidebar-item", open && "active")}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Icon name="bell" />
        <span>Notifications</span>
        {count > 0 && <span className="badge">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && <NotificationsPanel anchor={ref} panelRef={panelRef} onClose={() => setOpen(false)} />}
    </div>
  );
}

const PANEL_WIDTH = 360;
const GAP = 6;
const MARGIN = 8;

/**
 * Fixed position beside the anchor, kept on screen. The panel is rendered into
 * <body>: inside the sidebar it would be clipped by the sidebar's scroll box.
 */
function usePanelPosition(anchor: RefObject<HTMLElement | null>) {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(PANEL_WIDTH, window.innerWidth - 2 * MARGIN);
      const besideRight = r.right + GAP;
      const left = besideRight + width <= window.innerWidth - MARGIN ? besideRight : Math.max(MARGIN, window.innerWidth - width - MARGIN);
      // Below the button when there is no room beside it (narrow screens).
      const top = left === besideRight ? r.top : r.bottom + GAP;
      setPos({ top, left, width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor]);
  return pos;
}

function NotificationsPanel({
  anchor,
  panelRef,
  onClose,
}: {
  anchor: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
}) {
  const pos = usePanelPosition(anchor);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.notifications(false),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.notifications });
  const readOne = useMutation({ mutationFn: api.readNotification, onSettled: invalidate });
  const readAll = useMutation({ mutationFn: api.readAllNotifications, onSettled: invalidate });

  const open = (n: Notification) => {
    if (!n.read_at) readOne.mutate(n.id);
    onClose();
    navigate(`/p/${n.project_id}/tasks/${n.task_id}`);
  };

  if (!pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      className="popover notifications-panel"
      role="menu"
      style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: `calc(100vh - ${pos.top + MARGIN}px)` }}
    >
      <div className="popover-header">
        <strong>Notifications</strong>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => readAll.mutate()}
          disabled={readAll.isPending || !data?.some((n) => !n.read_at)}
        >
          Mark all read
        </button>
      </div>
      <div className="popover-body">
        {isLoading && <Spinner />}
        {data && data.length === 0 && <p className="muted empty">You're all caught up.</p>}
        {data?.map((n) => (
          <button
            key={n.id}
            className={cx("notification", !n.read_at && "unread")}
            onClick={() => open(n)}
            role="menuitem"
          >
            <span className="notification-dot" aria-hidden="true" />
            <span className="notification-main">
              <span className="notification-title">
                <span className="muted">#{n.task_number}</span> {KIND_LABEL[n.kind] ?? n.kind.replace(/_/g, " ")}
              </span>
              {n.body && <span className="notification-body">{n.body}</span>}
              <span className="notification-time muted">
                <RelativeTime iso={n.created_at} />
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}
